import React, { ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'

export type EmergencyEventIconVariant = 'with-background' | 'without-background'

export interface EmergencyEventIconProps {
    /**
     * `with-background` (default) = severity-coloured rounded 60px tile;
     * `without-background` = the bare icon glyph with no tile.
     */
    variant?: EmergencyEventIconVariant
    /**
     * Runtime severity-driven background colour for the `with-background` variant
     * (e.g. `getBackgroundColorFromSeverity(event.severity)`). It is a data value,
     * not a token, so it is applied as an inline `style` on the universal Box —
     * the established data-driven-colour pattern.
     */
    backgroundColor?: string
    /** The icon glyph to render centred in the tile. */
    children?: ReactNode
    className?: string
    testID?: string
}

/**
 * Emergency event icon tile (Figma EmergencyEventIcon 9100:64637): a 60×60 box
 * that, in the `with-background` variant, fills with the event's severity colour
 * and rounds its corners (4px), centring the icon glyph. The icon itself is
 * passed as children because the SVG registry lives in `aos-ui`; this primitive
 * only owns the tile chrome.
 */
export const EmergencyEventIcon: React.FC<EmergencyEventIconProps> = ({
    variant = 'with-background',
    backgroundColor,
    children,
    className,
    testID,
}) => {
    const withBackground = variant === 'with-background'

    return (
        <Box
            className={twMerge(
                'size-[60px] items-center justify-center',
                withBackground && 'rounded p-3.5',
                className,
            )}
            style={withBackground && backgroundColor ? { backgroundColor } : undefined}
            testID={testID}
        >
            {children}
        </Box>
    )
}

EmergencyEventIcon.displayName = 'EmergencyEventIcon'
